/**
 * The coach's side of page context and plan changes:
 *
 *   AttachBar      what's attached (chips, each removable) and the Attach
 *                  menu: the page they're on, or one of their players' plans
 *                  (D122). Nothing is attached without a tap.
 *   ConsentCard    asked in the app the first time a plan is attached (G3).
 *   ProposalCard   a proposed plan change, built by the server from the
 *                  validated change (never the model's prose), with Save, Not
 *                  now and Undo. The status shown is the server's answer (D123).
 */
import React, { useState } from "react";
import { Paperclip, X } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAMIQ_PLAYER_CONSENT_TITLE, BAMIQ_PLAYER_CONSENT_COPY } from "./consent";
import { bamiqPlayerAllowed, bamiqPlayerIds } from "../utils/featureFlags";

const ff = "'DM Sans', sans-serif";
const KIND_LABEL = { drill: "Drill", masterclass: "Masterclass", research: "Research", player: "Plan" };

const btn = (C, primary) => ({
  padding: "6px 11px", borderRadius: 8, fontFamily: ff, fontSize: 12.5, fontWeight: 800, cursor: "pointer",
  border: primary ? "none" : `1px solid ${C.border}`, background: primary ? GOLD : "transparent", color: primary ? "#111" : C.text,
});

export function chipText(a) {
  return a.type === "player" ? `${a.title || "Player"}'s plan` : a.title || KIND_LABEL[a.type];
}

export function AttachBar({ iq, page: pageItem, C, playerAllowed = bamiqPlayerAllowed, playersOn = bamiqPlayerIds().length > 0 }) {
  // A player's plan is offered only for players cleared for BAM IQ (D146).
  const page = pageItem?.type === "player" && !playerAllowed(pageItem.id) ? null : pageItem;
  const cleared = (iq.players || []).filter(p => playerAllowed(p.id));
  const canPickPlayer = playersOn && (iq.players ? cleared.length > 0 : true);
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const pageAttached = page && iq.attachments.some(a => a.type === page.type && String(a.id) === String(page.id));
  const choose = (item) => { iq.attach(item); setOpen(false); setPicking(false); };
  const pickPlayer = async () => { setPicking(true); if (!iq.players) await iq.loadPlayers(); };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 8, position: "relative" }}>
      {iq.attachments.map(a => (
        <span key={`${a.type}:${a.id}`} data-attachment={a.type}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: "100%", padding: "3px 4px 3px 9px", borderRadius: 999,
            border: `1px solid ${GOLD}55`, background: `${GOLD}14`, color: C.text, fontFamily: ff, fontSize: 12 }}>
          <span style={{ color: C.textMid, fontWeight: 800 }}>{a.type === "player" ? "Working on" : "Looking at"}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{chipText(a)}</span>
          <button type="button" className="btn-inline" aria-label={`Remove ${chipText(a)}`} onClick={() => iq.detach(a)}
            style={{ display: "grid", placeItems: "center", width: 20, height: 20, borderRadius: 999, border: "none", background: "transparent", color: C.textMid, cursor: "pointer" }}>
            <X size={12}/>
          </button>
        </span>
      ))}
      {page && !pageAttached && !open && (
        <button type="button" className="btn-inline" onClick={() => choose(page)} data-attach-page
          style={{ ...btn(C, false), padding: "3px 9px", fontSize: 12, fontWeight: 600, color: C.textMid, borderStyle: "dashed" }}>
          + Attach {KIND_LABEL[page.type]?.toLowerCase() || "page"}: {page.title}
        </button>
      )}
      <button type="button" className="btn-inline" aria-label="Attach" aria-expanded={open} onClick={() => { setOpen(o => !o); setPicking(false); }}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", color: C.textMid, cursor: "pointer", fontFamily: ff, fontSize: 12, padding: "3px 4px" }}>
        <Paperclip size={13}/> Attach
      </button>
      {open && (
        <div role="menu" style={{ position: "absolute", bottom: "100%", left: 0, marginBottom: 6, zIndex: 5, minWidth: 240, maxWidth: "100%", maxHeight: 280, overflowY: "auto",
          border: `1px solid ${C.border}`, borderRadius: 10, background: C.bgCard, boxShadow: C.elevatedShadow, padding: 6, display: "grid", gap: 2, fontFamily: ff }}>
          {!picking && page && !pageAttached && (
            <button type="button" role="menuitem" onClick={() => choose(page)} style={menuItem(C)}>This page: {page.title}</button>
          )}
          {!picking && canPickPlayer && <button type="button" role="menuitem" onClick={pickPlayer} style={menuItem(C)}>A player's plan…</button>}
          {picking && !iq.players && <div style={{ padding: 8, fontSize: 12.5, color: C.textMid }}>Loading your players…</div>}
          {picking && iq.players && !cleared.length && <div style={{ padding: 8, fontSize: 12.5, color: C.textMid }}>Players' plans aren't available in BAM IQ yet.</div>}
          {picking && cleared.map(p => (
            <button key={p.id} type="button" role="menuitem" data-player={p.id} onClick={() => choose({ type: "player", id: p.id, title: p.name })} style={menuItem(C)}>
              {p.name}{p.position ? <span style={{ color: C.textMid }}> · {p.position}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const menuItem = (C) => ({ textAlign: "left", padding: "8px 10px", borderRadius: 7, border: "none", background: "transparent", color: C.text, fontFamily: ff, fontSize: 13, cursor: "pointer" });

export function ConsentCard({ iq, C }) {
  return (
    <div role="group" aria-label={BAMIQ_PLAYER_CONSENT_TITLE} style={{ padding: "12px 14px", borderRadius: 10, border: `1px solid ${GOLD}55`, background: `${GOLD}10`, fontFamily: ff }}>
      <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 6 }}>{BAMIQ_PLAYER_CONSENT_TITLE}</div>
      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.55 }}>{BAMIQ_PLAYER_CONSENT_COPY}</div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button type="button" onClick={() => iq.answerConsent(true)} style={btn(C, true)}>Allow</button>
        <button type="button" onClick={() => iq.answerConsent(false)} style={btn(C, false)}>Not now</button>
      </div>
    </div>
  );
}

const MARK = { "+": "Add", "~": "Change", "-": "Remove" };
const STATUS_TEXT = {
  drafting: "BAM IQ is still writing. You can review and save this once the answer finishes.",
  rejected: "Not saved.",
  stale: "Not saved: the plan changed since this preview. Ask BAM IQ for a fresh proposal.",
  expired: "This proposal expired. Ask BAM IQ for a fresh one.",
  undone: "Undone. The plan is back as it was.",
  changed_since: "Can't undo: the plan has changed since this was saved.",
  too_old: "Too old to undo.",
  checkin_answered: "Can't undo: a check-in from it has already been answered.",
  preview_changed: "Not saved: this preview is out of date. Ask BAM IQ for a fresh proposal.",
  consent_required: "Not saved: BAM IQ needs your OK to work with players' plans.",
  off: "Plan changes from BAM IQ aren't switched on yet.",
  not_found: "That proposal isn't available.",
  failed: "Something went wrong. Nothing was saved.",
};

export function ProposalCard({ proposal: p, messageId, iq, C, playerName }) {
  const name = playerName || "the player";
  const groups = [];
  for (const row of p.preview || []) {
    let g = groups.find(x => x.section === row.section);
    if (!g) { g = { section: row.section, rows: [] }; groups.push(g); }
    g.rows.push(row);
  }
  const n = (p.preview || []).length;
  const color = { "+": C.green, "~": GOLD, "-": "#E57373" };
  return (
    <div data-proposal={p.action_id} data-status={p.status} style={{ marginTop: 12, border: `1px solid ${C.border}`, borderRadius: 12, background: C.bgHover, fontFamily: ff, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: C.textMid }}>Proposed change to {name}'s plan</div>
        {p.summary && <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginTop: 3 }}>{p.summary}</div>}
      </div>
      <div style={{ padding: "8px 14px", display: "grid", gap: 10 }}>
        {groups.map(g => (
          <div key={g.section}>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.textMid, marginBottom: 4 }}>{g.section}</div>
            <div style={{ display: "grid", gap: 4 }}>
              {g.rows.map((r, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "60px minmax(0, 1fr)", gap: 8, fontSize: 13.5, color: C.text, lineHeight: 1.45 }}>
                  <span style={{ color: color[r.change], fontWeight: 800, fontSize: 12 }}>{MARK[r.change]}</span>
                  <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                    {r.change === "~" ? (<><span style={{ color: C.textMid, textDecoration: "line-through" }}>{r.before}</span><br/>{r.after}</>)
                      : r.change === "-" ? <span style={{ textDecoration: "line-through" }}>{r.before}</span> : r.after}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: "10px 14px", borderTop: `1px solid ${C.border}`, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {p.status === "proposed" && (
          <>
            <button type="button" disabled={!!p.busy} onClick={() => iq.decide(messageId, p, "confirm")} style={{ ...btn(C, true), opacity: p.busy ? 0.6 : 1 }}>
              {p.busy === "confirm" ? "Saving…" : `Save ${n} change${n === 1 ? "" : "s"}`}
            </button>
            <button type="button" disabled={!!p.busy} onClick={() => iq.decide(messageId, p, "discard")} style={btn(C, false)}>Not now</button>
            <span style={{ fontSize: 12, color: C.textMid }}>Nothing is saved until you tap Save.</span>
          </>
        )}
        {p.status === "saved" && (
          <>
            <span role="status" style={{ fontSize: 13, color: C.green, fontWeight: 700 }}>Saved to {name}'s plan.</span>
            <button type="button" disabled={!!p.busy} onClick={() => iq.decide(messageId, p, "undo")} style={btn(C, false)}>{p.busy === "undo" ? "Undoing…" : "Undo"}</button>
          </>
        )}
        {p.status !== "proposed" && p.status !== "saved" && (
          <span role="status" style={{ fontSize: 13, color: C.textMid }}>{STATUS_TEXT[p.status] || STATUS_TEXT.failed}</span>
        )}
      </div>
    </div>
  );
}
